import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BsCloudUpload } from "react-icons/bs";
import { MdDeleteForever } from "react-icons/md";
import { GrFormAdd } from "react-icons/gr";
// import { FaArrowLeft } from "react-icons/fa";
import ActionButton from "../../Common/ActionButton";
import { InputField, SelectField, TextAreaField } from "../../Common/FormComponents";
import { useSession } from "../../../context/SessionContext";
import { getallMaterial } from "../../../Redux/actions/Operationsactions/getmaterial";
import { getallGatepass } from "../../../Redux/actions/Operationsactions/getgatepass";
import { getallStock } from "../../../Redux/actions/Operationsactions/getstock";
import { AddGatepassApi } from "../../../Api/Store/Gatepass";
import { filterStoreProjects } from "../projectFilters";
import {
  GetAllMaterialRequisitionsApi,
  GetMaterialRequisitionByIdApi,
} from "../../../Api/Store/MaterialRequisition";

/* Open quantity of an MR line in its packaging, floored to 4 dp (never rounded up).
   missing = packaging not found once materials are loaded. */
const lineCalc = (materials, line) => {
  let conv = 1;
  let missing = false;
  const loading = !!line.MaterialPackagingId && materials.length === 0;
  if (line.MaterialPackagingId) {
    const m = materials.find((x) => x.Id === line.MaterialId);
    const found = m?.Packagings?.find((p) => p.Id === line.MaterialPackagingId)?.ConversionToBase;
    conv = found || 1;
    missing = materials.length > 0 && !found;
  }
  const open = Math.floor((Number(line.OpenBase) / conv) * 1e4 + 1e-6) / 1e4;
  // Issuable now = stock reserved for this line + free stock (the backend takes reserved first, then free).
  const availBase = Number(line.ReservedBase || 0) + Number(line.FreeStock || 0);
  const avail = Math.min(open, Math.max(0, Math.floor((availBase / conv) * 1e4 + 1e-6) / 1e4));
  return { conv, open, avail, availBase, missing, loading, qty: line.Quantity ?? String(avail) };
};

/* Initial empty line item (standalone gate pass) */
const emptyLine = () => ({
  id: Date.now() + Math.random(),
  MaterialId: "",
  MaterialPackagingId: "",
  Quantity: "",
  Remarks: "",
});

const headerCellStyle = {
  height: "42px",
  paddingTop: "6px",
  paddingBottom: "6px",
  verticalAlign: "middle",
  lineHeight: "1.1",
};

/**
 * Add Gate Pass Form
 * Backend has no Edit/Delete endpoint for Gatepass — create-only.
 */
const AddGatePass = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { rolesdata } = useSession();
  const projects = filterStoreProjects(rolesdata?.data?.Projects);
  const materialData = useSelector((state) => state.getAllMaterial?.data || []);
  const stockData = useSelector((state) => state.getAllStock?.data || []);

  const [isLoading, setIsLoading] = useState(false);
  const [mrList, setMrList] = useState([]);
  const [mr, setMr] = useState(null);
  const [mrId, setMrId] = useState(location.state?.materialRequisitionId || "");

  useEffect(() => {
    dispatch(getallMaterial());
    dispatch(getallStock());
    GetAllMaterialRequisitionsApi("", "Approved,Partially Issued").then((res) => {
      if (res?.data?.IsSuccess) {
        setMrList(res.data.Data || []);
      } else {
        toast.error(res?.data?.Message || "Failed to load material requisitions");
      }
    });
  }, [dispatch]);

  /* Material.Id -> Stock row. Free = on hand minus stock reserved by open requisitions (base unit);
     a standalone gate pass may only take free stock. */
  const stockByMaterialId = useMemo(() => {
    const map = {};
    stockData.forEach((x) => {
      map[x.MaterialId] = x;
    });
    return map;
  }, [stockData]);
  const freeOf = (materialId) => {
    const row = stockByMaterialId[materialId];
    return Number(row ? (row.Free ?? row.Quantity) : 0) || 0;
  };

  /* ── Header form ── */
  const [formData, setFormData] = useState({
    ProjectId: "",
    GatepassDate: "",
    VehicleNumber: "",
    DriverName: "",
    Note: "",
  });

  /* ── Lines: the MR's open lines, or free-form rows when no MR is chosen ── */
  const [lineItems, setLineItems] = useState([emptyLine()]);

  /* Load the selected MR and build its lines in the MR line's packaging. */
  useEffect(() => {
    setMr(null);
    setLineItems([emptyLine()]);
    if (!mrId) {
      return;
    }
    setLineItems([]);
    let cancelled = false;
    GetMaterialRequisitionByIdApi(mrId).then((res) => {
      if (cancelled) {
        return;
      }
      if (!res?.data?.IsSuccess) {
        toast.error(res?.data?.Message || "Failed to load material requisition");
        return;
      }
      const data = res.data.Data;
      setMr(data);
      setLineItems(
        (data.Lines || [])
          .filter((l) => Number(l.OpenBase) > 0)
          .map((l) => ({ ...l, Quantity: undefined, Remarks: "" })), // Quantity = requested; lineCalc defaults to the open amount
      );
    });
    return () => {
      cancelled = true;
    };
  }, [mrId]);

  /* ── Handlers ── */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRow = () => {
    setLineItems((prev) => [...prev, emptyLine()]);
  };

  const handleLineMaterialChange = (idx, materialId) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], MaterialId: materialId, MaterialPackagingId: "" };
      return updated;
    });
  };

  const handleRemoveRow = (idx) => {
    setLineItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleLineChange = (idx, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (mrId && !mr) {
      toast.error("Material Requisition is still loading, please wait.");
      return;
    }
    if (!mrId && !formData.ProjectId) {
      toast.error("Project is required");
      return;
    }
    if (!formData.GatepassDate) {
      toast.error("Date is required");
      return;
    }
    if (!mrId) {
      if (lineItems.length === 0) {
        toast.error("Please add at least one line item");
        return;
      }
      for (let i = 0; i < lineItems.length; i++) {
        const item = lineItems[i];
        if (!item.MaterialId) {
          toast.error(`Material is required on row ${i + 1}`);
          return;
        }
        if (!item.Quantity || Number(item.Quantity) <= 0) {
          toast.error(`Quantity must be greater than 0 on row ${i + 1}`);
          return;
        }
        const material = materialData.find((m) => m.Id === item.MaterialId);
        const packaging = material?.Packagings?.find((p) => p.Id === item.MaterialPackagingId);
        const baseAvailable = freeOf(item.MaterialId);
        const availableInUnit =
          packaging && packaging.ConversionToBase
            ? baseAvailable / packaging.ConversionToBase
            : baseAvailable;
        if (Number(item.Quantity) > availableInUnit) {
          toast.error(
            `Row ${i + 1}: quantity exceeds available stock (${availableInUnit.toFixed(2)} ${
              packaging?.UomName || material?.UomName || ""
            })`,
          );
          return;
        }
      }
      try {
        setIsLoading(true);
        const res = await AddGatepassApi({
          projectId: formData.ProjectId,
          gatepassDate: formData.GatepassDate,
          vehicleNumber: formData.VehicleNumber,
          driverName: formData.DriverName,
          vehicleNo: formData.VehicleNumber,
          note: formData.Note,
          lines: lineItems.map((item) => ({
            materialId: item.MaterialId,
            materialPackagingId: item.MaterialPackagingId || null,
            quantity: Number(item.Quantity),
            remarks: item.Remarks,
          })),
        });
        if (res?.data?.IsSuccess) {
          toast.success(res.data.Message || "Gate Pass added successfully");
          dispatch(getallGatepass(formData.ProjectId));
          history.push("/store/gate-pass");
        } else {
          toast.error(res?.data?.Message || "Failed to add gate pass");
        }
      } catch (err) {
        toast.error("Something went wrong");
      } finally {
        setIsLoading(false);
      }
      return;
    }
    const calcs = lineItems.map((l) => lineCalc(materialData, l));
    if (!calcs.some((c) => Number(c.qty) > 0)) {
      toast.error("Enter a quantity greater than 0 on at least one line");
      return;
    }
    if (calcs.some((c) => c.loading)) {
      toast.error("Materials are still loading, please wait.");
      return;
    }
    for (let i = 0; i < lineItems.length; i++) {
      const { conv, open, avail, availBase, missing, qty } = calcs[i];
      if (missing) {
        toast.error(`Row ${i + 1}: packaging not found for this material`);
        return;
      }
      if (Number(qty) < 0 || Number(qty) * conv > Number(lineItems[i].OpenBase) + 1e-3) {
        toast.error(`Row ${i + 1}: quantity exceeds the open quantity (${open})`);
        return;
      }
      if (Number(qty) * conv > availBase + 1e-3) {
        toast.error(`Row ${i + 1}: only ${avail} is available in stock right now`);
        return;
      }
    }

    try {
      setIsLoading(true);

      const payload = {
        projectId: mr.ProjectId,
        materialRequisitionId: mrId,
        gatepassDate: formData.GatepassDate,
        vehicleNumber: formData.VehicleNumber,
        driverName: formData.DriverName,
        vehicleNo: formData.VehicleNumber,
        note: formData.Note,
        lines: lineItems
          .map((item, i) => ({ item, c: calcs[i] }))
          .filter(({ c }) => Number(c.qty) > 0)
          .map(({ item, c }) => {
            // Full remaining amount goes in exact base units so rounding never exceeds the open.
            const full = Number(c.qty) >= c.open;
            return {
              materialId: item.MaterialId,
              materialPackagingId: full ? null : item.MaterialPackagingId || null,
              quantity: full ? Number(item.OpenBase) : Number(c.qty),
              remarks: item.Remarks,
              materialRequisitionLineId: item.Id,
            };
          }),
      };

      const res = await AddGatepassApi(payload);
      if (res?.data?.IsSuccess) {
        toast.success(res.data.Message || "Gate Pass added successfully");
        dispatch(getallGatepass(mr.ProjectId));
        history.push("/store/gate-pass");
      } else {
        toast.error(res?.data?.Message || "Failed to add gate pass");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="row">
        <div className="col-md-12 text-left page_heading">
          <div className="d-flex align-items-center">
            {/* <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            /> */}
            <h4 className="m-0">Add Gate Pass</h4>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} autoComplete="off">
        {/* ── Header Fields ── */}
        <div className="row mb-2">
          <SelectField
            label="Material Requisition (Optional):"
            name="MaterialRequisitionId"
            value={mrId}
            onChange={(e) => setMrId(e.target.value)}
            options={[
              { value: "", label: "Select" },
              ...mrList.map((m) => ({
                value: m.Id,
                label: `${m.MrNumber}${m.ProjectName ? ` — ${m.ProjectName}` : ""}`,
              })),
            ]}
            stacked
            wrapperCol="col-xl-4 col-lg-4 col-md-6 col-12"
          />
          {mrId ? (
            <InputField
              label="Project:"
              name="ProjectName"
              value={mr?.ProjectName || ""}
              readOnly
              stacked
              wrapperCol="col-xl-4 col-lg-4 col-md-6 col-12"
            />
          ) : (
            <SelectField
              label="Project:"
              name="ProjectId"
              value={formData.ProjectId}
              onChange={handleFormChange}
              options={[
                { value: "", label: "Select" },
                ...projects.map((p) => ({ value: p.ProjectId, label: p.ProjectName })),
              ]}
              required
              stacked
              wrapperCol="col-xl-4 col-lg-4 col-md-6 col-12"
            />
          )}

          <InputField
            label="Gate Pass Date:"
            name="GatepassDate"
            type="date"
            value={formData.GatepassDate}
            onChange={handleFormChange}
            required
            stacked
            wrapperCol="col-xl-4 col-lg-4 col-md-6 col-12"
          />
          <InputField
            label="Vehicle Number:"
            name="VehicleNumber"
            value={formData.VehicleNumber}
            onChange={handleFormChange}
            placeholder="e.g. LES-1234"
            stacked
            wrapperCol="col-xl-4 col-lg-4 col-md-6 col-12"
          />

          <InputField
            label="Driver Name:"
            name="DriverName"
            value={formData.DriverName}
            onChange={handleFormChange}
            placeholder="Enter driver name"
            stacked
            wrapperCol="col-xl-4 col-lg-4 col-md-6 col-12"
          />
          <TextAreaField
            label="Note / Purpose:"
            name="Note"
            value={formData.Note}
            onChange={handleFormChange}
            rows={2}
            stacked
            wrapperCol="col-xl-4 col-lg-4 col-md-6 col-12"
          />
        </div>

        {/* ── Line Items Table ── */}
        <div className="row mt-4 clearfix">
          <div className="col-md-12 p-0 column">
            <div className="table-responsive">
              <table
                className="table table-bordered table-hover"
                id="tab_logic"
                style={{ fontSize: "13px" }}
              >
                {mrId ? (
                  <>
                    <thead style={{ backgroundColor: "#E8FBFB" }}>
                      <tr style={{ height: "42px" }}>
                        <th className="text-center" style={{ ...headerCellStyle, width: 50 }}>
                          Sr#
                        </th>
                        <th
                          className="text-center"
                          style={{ ...headerCellStyle, minWidth: "180px" }}
                        >
                          Material
                        </th>
                        <th
                          className="text-center"
                          style={{ ...headerCellStyle, minWidth: "130px" }}
                        >
                          Packaging
                        </th>
                        <th className="text-center" style={{ ...headerCellStyle, width: "130px" }}>
                          Open Qty
                        </th>
                        <th className="text-center" style={{ ...headerCellStyle, width: "150px" }}>
                          Available Now
                        </th>
                        <th className="text-center" style={{ ...headerCellStyle, width: "170px" }}>
                          Issue Qty <span className="estaric">*</span>
                        </th>
                        <th
                          className="text-center"
                          style={{ ...headerCellStyle, minWidth: "150px" }}
                        >
                          Remarks
                        </th>
                        <th className="text-center" style={{ ...headerCellStyle, width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody id="tablebody">
                      {lineItems.map((item, idx) => {
                        const { conv, open, avail, qty, loading } = lineCalc(materialData, item);
                        const unit = item.PackagingName || item.BaseUomName || "";
                        return (
                          <tr key={item.Id} id={`addr${idx}`}>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              {idx + 1}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>{item.MaterialName}</td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              {item.PackagingName || `Base (${item.BaseUomName || ""})`}
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              {loading ? "Loading…" : `${open} ${unit}`}
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              {loading ? "Loading…" : `${avail} ${unit}`}
                              {!loading && avail < open && (
                                <small className="d-block text-warning">
                                  {Number((open - avail).toFixed(4))} {unit} still to be purchased
                                </small>
                              )}
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <input
                                type="number"
                                name="Quantity"
                                value={qty}
                                onChange={(e) => handleLineChange(idx, "Quantity", e.target.value)}
                                className="input_styling text-right"
                                placeholder="0"
                                min={0}
                                max={avail}
                                disabled={loading}
                                step="any"
                              />
                              {item.MaterialPackagingId && !loading && Number(qty) > 0 && (
                                <small className="text-muted">
                                  = {Number((Number(qty) * conv).toFixed(4))} {item.BaseUomName}
                                </small>
                              )}
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <input
                                type="text"
                                name="Remarks"
                                value={item.Remarks || ""}
                                onChange={(e) => handleLineChange(idx, "Remarks", e.target.value)}
                                className="input_styling"
                                placeholder="Remarks"
                              />
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <span onClick={() => handleRemoveRow(idx)}>
                                <MdDeleteForever
                                  size="1.3em"
                                  style={{ cursor: "pointer", color: "#dc3545" }}
                                  title="Remove"
                                />
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </>
                ) : (
                  <>
                    <thead style={{ backgroundColor: "#E8FBFB" }}>
                      <tr style={{ height: "42px" }}>
                        <th className="text-center" style={{ ...headerCellStyle, width: 50 }}>
                          Sr#
                        </th>
                        <th
                          className="text-center"
                          style={{ ...headerCellStyle, minWidth: "180px" }}
                        >
                          Material <span className="estaric">*</span>
                        </th>
                        <th
                          className="text-center"
                          style={{ ...headerCellStyle, minWidth: "150px" }}
                        >
                          Packaging (optional)
                        </th>
                        <th className="text-center" style={{ ...headerCellStyle, width: "150px" }}>
                          Available Stock
                        </th>
                        <th className="text-center" style={{ ...headerCellStyle, width: "150px" }}>
                          Issue Qty <span className="estaric">*</span>
                        </th>
                        <th className="text-center" style={{ ...headerCellStyle, width: "120px" }}>
                          Unit (UOM)
                        </th>
                        <th
                          className="text-center"
                          style={{ ...headerCellStyle, minWidth: "150px" }}
                        >
                          Remarks
                        </th>
                        <th className="text-center" style={{ ...headerCellStyle, width: 50 }}>
                          <span onClick={handleAddRow} style={{ cursor: "pointer" }}>
                            <GrFormAdd className="add_icon drow" size="1em" />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody id="tablebody">
                      {lineItems.map((item, idx) => {
                        const selectedMaterial = materialData.find((m) => m.Id === item.MaterialId);
                        const packagingOptions = selectedMaterial?.Packagings || [];
                        const baseAvailable = freeOf(item.MaterialId);
                        const selectedPackaging = packagingOptions.find(
                          (p) => p.Id === item.MaterialPackagingId,
                        );
                        const unitLabel =
                          selectedPackaging?.UomName || selectedMaterial?.UomName || "";
                        const availableInSelectedUnit =
                          selectedPackaging && selectedPackaging.ConversionToBase
                            ? baseAvailable / selectedPackaging.ConversionToBase
                            : baseAvailable;

                        return (
                          <tr key={item.id || idx} id={`addr${idx}`}>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              {idx + 1}
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <select
                                name="MaterialId"
                                value={item.MaterialId || ""}
                                onChange={(e) => handleLineMaterialChange(idx, e.target.value)}
                                className="input_styling"
                              >
                                <option value="">Select Material</option>
                                {materialData.map((m) => {
                                  const qty = freeOf(m.Id);
                                  return (
                                    <option key={m.Id} value={m.Id} disabled={qty <= 0}>
                                      {qty > 0
                                        ? `${m.ItemName} — ${qty} ${m.UomName || ""} available`
                                        : `${m.ItemName} — Out of stock`}
                                    </option>
                                  );
                                })}
                              </select>
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <select
                                name="MaterialPackagingId"
                                value={item.MaterialPackagingId || ""}
                                onChange={(e) =>
                                  handleLineChange(idx, "MaterialPackagingId", e.target.value)
                                }
                                className="input_styling"
                                disabled={packagingOptions.length === 0}
                              >
                                <option value="">
                                  {selectedMaterial
                                    ? `Base (${selectedMaterial.UomName})`
                                    : "Base UOM"}
                                </option>
                                {packagingOptions.map((p) => (
                                  <option key={p.Id} value={p.Id}>
                                    {p.PackagingName} ({p.UomName})
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <input
                                type="text"
                                readOnly
                                className="input_styling text-center"
                                style={{
                                  backgroundColor: "#f8f9fa",
                                  cursor: "default",
                                  fontWeight: 600,
                                  color:
                                    item.MaterialId && availableInSelectedUnit > 0
                                      ? "#1e293b"
                                      : "#dc3545",
                                }}
                                value={
                                  item.MaterialId
                                    ? `${Number(availableInSelectedUnit.toFixed(2)).toLocaleString()} ${unitLabel}`
                                    : "-"
                                }
                              />
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <input
                                type="number"
                                name="Quantity"
                                value={item.Quantity ?? ""}
                                onChange={(e) => handleLineChange(idx, "Quantity", e.target.value)}
                                className="input_styling text-right"
                                placeholder="0"
                                min={0}
                                max={availableInSelectedUnit || undefined}
                                step="0.01"
                              />
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <input
                                type="text"
                                readOnly
                                className="input_styling text-center"
                                style={{
                                  backgroundColor: "#f8f9fa",
                                  cursor: "default",
                                  fontWeight: 500,
                                }}
                                value={item.MaterialId ? unitLabel || "-" : "-"}
                              />
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <input
                                type="text"
                                name="Remarks"
                                value={item.Remarks || ""}
                                onChange={(e) => handleLineChange(idx, "Remarks", e.target.value)}
                                className="input_styling"
                                placeholder="Remarks"
                              />
                            </td>
                            <td className="text-center" style={{ verticalAlign: "middle" }}>
                              <span onClick={() => handleRemoveRow(idx)}>
                                <MdDeleteForever
                                  size="1.3em"
                                  style={{ cursor: "pointer", color: "#dc3545" }}
                                  title="Remove"
                                />
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </>
                )}
              </table>
            </div>
          </div>
        </div>

        {/* ── Footer Buttons ── */}
        <div className="float-right mt-4 mr-md-3">
          <ActionButton
            text="Cancel"
            variant="cancel"
            onClick={() => history.push("/store/gate-pass")}
          />
          <ActionButton text="Save" icon={BsCloudUpload} type="submit" isLoading={isLoading} />
        </div>
      </form>
    </div>
  );
};

export default AddGatePass;
